import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Divider, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import Routes from 'constants/routes';
import { currencyCode } from 'appConfig';

import { AppDispatch, RootState } from 'redux/store';
import { fetchTransactionDetails, issueRefund, IssueRefundPayload } from 'redux/slices/transaction/transactionDetails';
import TextInput from 'components/Common/TextInput';
import Button from 'components/Common/Button';
import Modal from 'components/Common/Modal';
import AssistanceFooter from 'components/Common/AssistanceFooter';
import CardWrapper from 'components/Common/CardWrapper';
import MuiLoader from 'components/Common/Loader';
import { formatAmount } from 'utils/helpers';

const SEND_REFUND_TITLE = 'Issue Refund';
const SEND_REFUND_DESCRIPTION =
    'Choose the amount you would like to refund. Be advised that once you issue the refund you cannot reverse the transaction.';

export default function IssueRefund() {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { id } = useParams();
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const { transaction, merchant, sva } = useSelector((state: RootState) => ({
        transaction: state.transaction,
        merchant: state.user.defaultMerchant,
        sva: state.wallet.moneyContainer.defaultSVA.containerId,
    }));
    const { details, loading: initialLoading } = transaction || {};
    const { companyCode } = merchant || {};
    const { amount, consumer } = details || {};
    const { username, uuid } = consumer || {};
    const { watch, control, handleSubmit, getValues } = useForm<IssueRefundPayload>({
        defaultValues: {
            amount: amount || 0,
            currencyCode: currencyCode || '',
            companyCode: companyCode || '',
            username: username || '',
            svaFrom: sva ? String(sva) : '',
        },
    });

    useEffect(() => {
        id && dispatch(fetchTransactionDetails(id));
    }, [id, dispatch]);

    const onSubmit = (formValues: IssueRefundPayload) => {
        setModalOpen(true);
    };

    const handleConfirm = async () => {
        const { amount, ...values }: IssueRefundPayload = getValues();
        try {
            setLoading(true);
            const { payload } = await dispatch(issueRefund({ ...values, amount: Number(amount) }));
            setLoading(false);
            if (payload?.code === 200) {
                navigate(Routes.community.ParishonerDetails.replace(':uuid', uuid));
            }
        } catch (err) {
            setLoading(false);
        }
    };
    return (
        <CardWrapper>
            <MuiLoader absolute loading={initialLoading} />
            <Typography sx={{ fontSize: 26, mb: 2, fontWeight: 400 }} variant="h1">
                {SEND_REFUND_TITLE}
            </Typography>
            <Divider />
            <Typography sx={{ mt: 2, mb: 4, fontSize: { xs: 12, sm: 18 } }} variant="body1">
                {SEND_REFUND_DESCRIPTION}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    style={{ width: '100%', marginTop: 12, marginBottom: 2, display: 'flex', alignItems: 'center' }}
                >
                    <TextInput icon={null} label="Reason for refund" sx={{ width: { xs: '80%', sm: '100%' } }} />

                    <Controller
                        name="amount"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                sx={{ mt: 2, mb: 2, ml: { sm: 3 } }}
                                width="40%"
                                value={formatAmount(amount)}
                                placeholder="Please enter the amount."
                                iconPosition="end"
                                icon={null}
                                label="Amount"
                                disabled={!!amount}
                            />
                        )}
                    />
                </form>

                <TextInput
                    icon={null}
                    multiline
                    minRows={5}
                    sx={{ mt: 2, mb: 22 }}
                    width="100%"
                    placeholder="Type optional note here..."
                    label="Note"
                />

                <Button
                    sx={{ justifyContent: 'center', mb: 6, width: { xs: '70%', lg: '50%' } }}
                    title="Refund"
                    type="submit"
                    onClick={onSubmit}
                />
            </Box>
            <AssistanceFooter type="Issue with this Transaction?" />

            {/* Confirm Invoice Modal */}
            <Modal onClose={() => setModalOpen(false)} isOpen={modalOpen} modalTitle="Confirm Refund Invoice">
                <>
                    <Typography sx={{ fontSize: { xs: 12, sm: 16 } }}>
                        Are you sure you want to refund an invoice for <br />
                        <strong style={{ marginBottom: 2, fontSize: 20 }}>{formatAmount(watch('amount'))}</strong>
                    </Typography>
                    <Typography style={{ whiteSpace: 'nowrap' }}>
                        <p style={{ color: '#1F2937', marginTop: 3, marginBottom: 3 }}>To</p>
                        <strong style={{ fontSize: 20 }}>{watch('username')}</strong>
                    </Typography>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', marginTop: 32 }}>
                        <Button onClick={() => setModalOpen(false)} width={'45%'} title="Cancel" />
                        <Button loading={loading} onClick={() => handleConfirm()} width={'45%'} title="Confirm" />
                    </div>
                </>
            </Modal>
        </CardWrapper>
    );
}
